import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { BookOpen, Feather, Clock, Heart, Mail, Check, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { toast } from 'sonner';

const Publicaciones = () => {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !consent) {
      toast.error('Por favor, completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/publicaciones/waitlist', { email, consent });
      if (response.data.already_subscribed) {
        toast.info('Ya estás en la lista. Te avisaré cuando publique.');
      } else {
        setSubmitted(true);
      }
    } catch (error) {
      toast.error('Hubo un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      <Helmet>
        <title>Manuales de Conexión | Escuela de Consciencia Akáshica</title>
        <meta name="description" content="Escritos para leer despacio, volver cuando lo necesites, y acompañar lo que ya está en proceso. Manuales breves y profundos sobre consciencia, integración y autosostén." />
      </Helmet>

      {/* ============ HERO ============ */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-b from-[hsl(var(--sage-light))] via-white to-white overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-[hsl(var(--accent))]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-[hsl(var(--sage))]/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[hsl(var(--accent))]/20 to-[hsl(var(--sage-light))] mb-8 shadow-lg">
            <Feather className="w-10 h-10 text-[hsl(var(--accent))]" strokeWidth={1.5} />
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] mb-8 leading-tight">
            Manuales de conexión
          </h1>
          
          <p className="text-lg sm:text-xl text-[hsl(var(--text-secondary))] leading-relaxed">
            Escritos para leer despacio,<br />
            volver cuando lo necesites,<br />
            y acompañar lo que ya está en proceso.
          </p>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-3 mt-10">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-[hsl(var(--accent))]/50" />
            <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-[hsl(var(--accent))]/50" />
          </div>
        </div>
      </section>

      {/* ============ QUÉ SON ============ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
              Qué son los Manuales de conexión
            </h2>
          </div>
          
          <div className="space-y-6 text-[hsl(var(--text-secondary))] leading-relaxed">
            <p>
              No son libros para aprender más.<br />
              Son textos para <em className="text-[hsl(var(--accent))] not-italic font-medium">integrar</em> mejor.
            </p>
            
            <p>
              Escritos breves y profundos que nacen de la práctica real 
              —de años acompañando procesos, observando patrones, 
              y traduciendo lo sutil a palabras que se puedan habitar.
            </p>
            
            <div className="bg-gradient-to-r from-[hsl(var(--sage-light))] to-[hsl(var(--sage-light))]/50 rounded-2xl p-6 border-l-4 border-[hsl(var(--sage))]">
              <p className="text-[hsl(var(--text-primary))]">
                Cada manual aborda un tema concreto:<br />
                <span className="text-[hsl(var(--sage))] font-medium">límites, sostén, presencia, regulación, vínculo, luz…</span><br />
                No para explicarlo todo, sino para iluminar un punto 
                y dejarte más claro que antes.
              </p>
            </div>
            
            <p className="pt-4">
              Son <em className="text-[hsl(var(--accent))] not-italic font-medium">compañía silenciosa</em>.<br />
              Lo que lees cuando no estás en formación.<br />
              Cuando necesitas volver a un concepto.<br />
              Cuando quieres profundizar sin acompañamiento grupal.
            </p>
          </div>
        </div>
      </section>

      {/* ============ CUÁNDO ACUDIR ============ */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-[hsl(var(--cream))] to-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--sage))]/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[hsl(var(--sage))]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
              Cuándo acudir a ellos
            </h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[hsl(var(--border))] hover:shadow-md transition-shadow">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--accent))]/20 to-[hsl(var(--accent))]/5 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[hsl(var(--text-primary))] font-medium mb-1">
                    Cuando algo resuena pero no termina de ordenarse
                  </p>
                  <p className="text-[hsl(var(--text-secondary))] text-sm leading-relaxed">
                    Un manual puede darte la palabra exacta que te faltaba.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[hsl(var(--border))] hover:shadow-md transition-shadow">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--sage))]/20 to-[hsl(var(--sage))]/5 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[hsl(var(--text-primary))] font-medium mb-1">
                    Cuando necesitas volver a un concepto sin repetir un curso
                  </p>
                  <p className="text-[hsl(var(--text-secondary))] text-sm leading-relaxed">
                    Para releer, subrayar, integrar a tu ritmo.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[hsl(var(--border))] hover:shadow-md transition-shadow">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--accent))]/10 to-[hsl(var(--sage))]/10 flex items-center justify-center">
                  <Feather className="w-6 h-6 text-[hsl(var(--text-secondary))]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[hsl(var(--text-primary))] font-medium mb-1">
                    Cuando quieres profundizar en silencio
                  </p>
                  <p className="text-[hsl(var(--text-secondary))] text-sm leading-relaxed">
                    Sin grupo, sin fechas, sin estructura externa.<br />
                    Solo tú y el texto.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FILOSOFÍA ============ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
              Cómo se escribe aquí
            </h2>
          </div>
          
          <div className="space-y-6 text-[hsl(var(--text-secondary))] leading-relaxed">
            <p>
              Estos manuales no se publican por calendario.<br />
              Se escriben cuando hay algo verdadero que compartir.
            </p>
            
            <p className="text-[hsl(var(--text-tertiary))] italic">
              No hay catálogo que llenar.<br />
              No hay ritmo de producción.<br />
              No hay presión de novedades.
            </p>
            
            <p>
              Solo escritura honesta, revisada,<br /> 
              y compartida cuando está lista.
            </p>
            
            <div className="pt-6 mt-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-[hsl(var(--sage-light))] rounded-2xl p-5">
                  <p className="text-[hsl(var(--text-primary))] font-medium mb-1">Formato</p>
                  <p className="text-sm text-[hsl(var(--sage))]">Digital (PDF)</p>
                </div>
                <div className="bg-[hsl(var(--accent))]/10 rounded-2xl p-5">
                  <p className="text-[hsl(var(--text-primary))] font-medium mb-1">Extensión</p>
                  <p className="text-sm text-[hsl(var(--accent))]">Breve, sin relleno</p>
                </div>
                <div className="bg-[hsl(var(--sage-light))] rounded-2xl p-5">
                  <p className="text-[hsl(var(--text-primary))] font-medium mb-1">Acceso</p>
                  <p className="text-sm text-[hsl(var(--sage))]">Para siempre</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ESTADO ACTUAL + CTA ============ */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-[hsl(var(--sage-light))]/50 to-[hsl(var(--cream))]">
        <div className="max-w-xl mx-auto px-6 text-center">
          {/* Estado */}
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-5 py-2.5 mb-8 shadow-sm border border-[hsl(var(--sage))]/20">
            <div className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--sage))] animate-pulse" />
            <span className="text-sm text-[hsl(var(--sage))] font-medium">En gestación</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
            Este espacio está en gestación
          </h2>
          
          <p className="text-[hsl(var(--text-secondary))] mb-10 leading-relaxed">
            Los primeros manuales están escribiéndose.<br />
            Cuando haya algo disponible, lo sabrás.
          </p>
          
          {/* Formulario o mensaje de éxito */}
          {submitted ? (
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-[hsl(var(--sage))]/20">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[hsl(var(--sage))] to-[hsl(var(--sage))]/80 flex items-center justify-center mx-auto mb-5 shadow-md">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-3">
                Gracias. Te avisaré.
              </h3>
              <p className="text-[hsl(var(--text-secondary))] text-sm leading-relaxed">
                Cuando haya un manual disponible, recibirás un email.<br />
                Mientras tanto, no te escribiré.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-[hsl(var(--border))]">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center mx-auto mb-5">
                <Mail className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
              <h3 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-6">
                Avísame cuando publiques
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-tertiary))]" />
                  <Input 
                    type="email"
                    placeholder="Tu email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 py-6 bg-[hsl(var(--sage-light))]/50 border-[hsl(var(--border))] focus:border-[hsl(var(--accent))] focus:ring-[hsl(var(--accent))]/20 rounded-xl"
                    required
                  />
                </div>
                
                <div className="flex items-start gap-3 text-left">
                  <Checkbox 
                    id="consent"
                    checked={consent}
                    onCheckedChange={setConsent}
                    className="mt-1 border-[hsl(var(--sage))]"
                  />
                  <label htmlFor="consent" className="text-sm text-[hsl(var(--text-secondary))] leading-relaxed cursor-pointer">
                    Acepto recibir un aviso cuando se publique un manual. Nada más. Sin newsletter, sin promociones.
                    Consulta la{' '}
                    <Link to="/privacidad" target="_blank" className="text-[#c9a227] hover:underline">Política de privacidad</Link>.
                  </label>
                </div>
                
                <Button 
                  type="submit"
                  disabled={loading || !email || !consent}
                  className="w-full bg-gradient-to-r from-[hsl(var(--accent))] to-[hsl(var(--accent))]/90 hover:from-[hsl(var(--accent))]/90 hover:to-[hsl(var(--accent))]/80 text-white py-6 rounded-xl font-medium shadow-md hover:shadow-lg transition-all"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Quiero que me avises'
                  )}
                </Button>
              </form>
              
              <p className="text-xs text-[hsl(var(--text-tertiary))] mt-5">
                No necesitas hacer nada ahora.<br />
                Solo deja tu email si quieres que te avise.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ============ NOTA DE CIERRE ============ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-[hsl(var(--accent))]/30" />
            <Feather className="w-5 h-5 text-[hsl(var(--accent))]/50" />
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-[hsl(var(--accent))]/30" />
          </div>
          <p className="text-[hsl(var(--text-tertiary))] italic leading-relaxed">
            Este espacio se irá llenando poco a poco.<br />
            Sin prisa. Sin calendario.<br />
            Cuando haya algo que merezca ser leído, estará aquí.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Publicaciones;
